import React, { useCallback, useEffect, useState } from "react";
import { getDeletedContacts, restoreContact } from "../api/client.js";
import CrmDeletedContactDrawer from "./CrmDeletedContactDrawer.jsx";
import { formatContactType, formatDateTime } from "./formatters.js";

const RECENT_DAYS = 30;

export default function CrmDeletedContactsPage() {
  const [deletedContacts, setDeletedContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [restoreError, setRestoreError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const fetchDeletedContacts = useCallback(() => {
    setLoading(true);
    setError("");
    getDeletedContacts({ sinceDays: RECENT_DAYS })
      .then((entries) => {
        setDeletedContacts(Array.isArray(entries) ? entries : []);
      })
      .catch((err) => {
        setError(err.message || "Unable to load deleted contacts");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchDeletedContacts();
  }, [fetchDeletedContacts]);

  useEffect(() => {
    if (!statusMessage) return undefined;
    const timer = setTimeout(() => setStatusMessage(""), 3300);
    return () => clearTimeout(timer);
  }, [statusMessage]);

  const handleRestore = async (contactId) => {
    setRestoringId(contactId);
    setRestoreError("");
    try {
      await restoreContact(contactId);
      setDeletedContacts((prev) => prev.filter((contact) => contact.id !== contactId));
      setStatusMessage("Contact restored");
      setSelectedContact(null);
    } catch (err) {
      setRestoreError(err.message || "Unable to restore contact");
    } finally {
      setRestoringId(null);
    }
  };

  const handleRetry = () => {
    fetchDeletedContacts();
  };

  const hasEntries = deletedContacts.length > 0;

  return (
    <>
      <div className="space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Admin • Deleted contacts</p>
            <h1 className="text-3xl font-semibold text-white">Deleted contacts</h1>
            <p className="text-sm text-slate-400">Showing records deleted within the last {RECENT_DAYS} days.</p>
          </div>
          <div className="min-w-[220px] text-right text-xs uppercase tracking-[0.3em] text-emerald-200">
            {statusMessage || " "}
          </div>
        </header>
        {error && (
          <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
            <p>{error}</p>
            <button
              type="button"
              onClick={handleRetry}
              className="mt-2 text-sm font-semibold text-rose-200 underline"
            >
              Retry
            </button>
          </div>
        )}
        <section className="rounded-3xl border border-white/10 bg-white/5 p-6">
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-400">Loading deleted contacts…</div>
          ) : hasEntries ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {deletedContacts.map((contact) => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => {
                    setSelectedContact(contact);
                    setRestoreError("");
                  }}
                  className="flex flex-col rounded-2xl border border-white/10 bg-slate-950/80 p-5 text-left transition hover:border-rose-400/40"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-white">
                        {contact.name || "Unnamed contact"}
                      </h3>
                      <p className="text-xs uppercase tracking-[0.35em] text-slate-500">
                        {formatContactType(contact.type)}
                      </p>
                    </div>
                    <span className="text-[10px] rounded-full border border-rose-500/40 bg-rose-500/10 px-3 py-1 font-semibold uppercase tracking-[0.3em] text-rose-200">
                      Deleted
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-slate-300">
                    {contact.emails?.[0] || contact.phones?.[0] || "No email or phone"}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-400">
                    <span className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1">
                      {contact.listingsOwned?.length || 0} listings
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1">
                      Deleted {formatDateTime(contact.deletedAt)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-slate-400">No deleted contacts in the last {RECENT_DAYS} days.</div>
          )}
        </section>
      </div>
      <CrmDeletedContactDrawer
        contact={selectedContact}
        open={Boolean(selectedContact)}
        onClose={() => setSelectedContact(null)}
        onRestore={handleRestore}
        restoring={restoringId === selectedContact?.id}
        error={restoreError}
      />
    </>
  );
}
